import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  BranchListItemResponse,
  CatalogIndustrySurchargeResponse,
  CatalogService,
  CategoriesResponseSchema,
  Category,
  CustomerDepartmentQualificationsListItemResponse,
  CustomerDepartmentService,
  CustomerLocationDepartmentService,
  DepartmentWithDutyModelsListItemResponse,
  EmptySuccessfulResponse,
  ListApiResponse,
  NotificationRecipientsUpdateRequest,
  NotificationsListResponse,
  PaginatedApiResponse,
  PspBranchService,
  PspCustomerLocationService,
  PspCustomerService,
  PspCustomerSurchargeResponse,
  PspService,
  QualificationPricingListItemResponse,
  QualificationsListItemResponse,
  RequirementCategoryResponse,
  RequirementResponse,
  ShiftTimesResponse,
  UserItemResponse,
  UserProfileResponse,
  UserProfileSettingsUpdateRequest,
  UserProfileUpdateRequest,
  UserService,
} from '../../../api';
import { BaseApplicationTagType } from '../enums/base-application.enum';
import { UserItemFullNameType } from '../../modules/candidates/_types/base-user-full-name.type';

@Injectable({ providedIn: 'root' })
export class BaseSharedService {
  constructor(
    private readonly catalogService: CatalogService,
    private readonly pspBranchService: PspBranchService,
    private readonly userService: UserService,
    private readonly pspService: PspService,
    private readonly customerDepartmentService: CustomerDepartmentService,
    private readonly customerLocationDepartmentService: CustomerLocationDepartmentService,
    private pspCustomerService: PspCustomerService,
    private pspCustomerLocationService: PspCustomerLocationService
  ) {}

  loadQualifications(application: BaseApplicationTagType): Observable<QualificationsListItemResponse[]> {
    return this.catalogService
      .getAppApiWebV1CatalogsQualificationListIndex({ tag: application })
      .pipe(map((response) => response.data.qualifications));
  }

  loadQualificationsCategories(application: BaseApplicationTagType): Observable<Array<Category>> {
    return this.catalogService.getAppApiWebV1CatalogsQualificationCategoryListIndex({ tag: application }).pipe(
      take(1),
      map((response) => response.data.categories)
    );
  }

  loadRequirements(application: BaseApplicationTagType): Observable<Array<RequirementCategoryResponse>> {
    return this.catalogService.getAppApiWebV1CatalogsRequirementListIndex({ tag: application }).pipe(
      take(1),
      map((response) => response.data.categories)
    );
  }

  convertRequirementCategoriesToRequirements(categories: RequirementCategoryResponse[]): RequirementResponse[] {
    return categories.flatMap((category) => category.requirements);
  }

  getPspBranches(orgId: number, active?: any): Observable<Array<BranchListItemResponse>> {
    return this.pspBranchService
      .getPspBranchListOfBranches({ psp: orgId, active })
      .pipe(map((response) => response.data));
  }

  getProfile(): Observable<UserProfileResponse | undefined> {
    return this.userService.getAppApiWebV1UserUserprofileShow().pipe(map((response) => response.data));
  }

  updateProfile(body: UserProfileUpdateRequest): Observable<EmptySuccessfulResponse> {
    return this.userService.putAppApiWebV1UserUserprofileUpdate({ body }).pipe(take(1));
  }

  getCustomerDepartmentList(
    customerId: number | string,
    active?: boolean
  ): Observable<DepartmentWithDutyModelsListItemResponse[]> {
    return this.customerDepartmentService
      .getCustomerDepartmentListOfDepartments({ customer: customerId as number, active })
      .pipe(map((response) => response.data));
  }

  getCustomerDepartmentUsers(customer: number, department: number): Observable<Array<UserItemResponse>> {
    return this.customerDepartmentService
      .getCustomersDepartmentUsersList({ customer, department })
      .pipe(map((response) => response.data));
  }

  getCustomerDepartmentQualificationsList(
    customer: number,
    department: number,
    category?: number,
    tag?: BaseApplicationTagType,
    search?: string
  ): Observable<{
    qualifications?: Array<CustomerDepartmentQualificationsListItemResponse>;
  }> {
    return this.customerDepartmentService
      .getCustomerDepartmentQualificationsList({ customer, department, category, tag, search })
      .pipe(map((response) => response.data));
  }

  getDocumentCategories(): Observable<CategoriesResponseSchema> {
    return this.catalogService.getDocumentsCategories().pipe(map((response) => response.data));
  }

  getPspPriceList(params: {
    tag?: BaseApplicationTagType;
    psp: number;
  }): Observable<QualificationPricingListItemResponse[]> {
    return this.pspService
      .getAppApiWebV1PspPriceListIndex(params)
      .pipe(map((response) => response.data.qualifications));
  }

  prepareUserProfileUpdateData<T>(profile: any): T {
    profile = JSON.parse(JSON.stringify(profile));

    profile.avatar = profile.avatar ? { data: profile.avatar } : undefined;

    if (profile.password && profile.newPassword && profile.newPasswordRepeat) {
      profile.password = {
        password: profile.password,
        newPassword: profile.newPassword,
        newPasswordRepeat: profile.newPasswordRepeat,
      };

      delete profile.newPassword;
      delete profile.newPasswordRepeat;
    } else {
      delete profile.password;
    }

    return profile;
  }

  getDefaultShiftsTimes(): Observable<ShiftTimesResponse> {
    return this.catalogService.getApiCatalogsShiftTimes().pipe(map((response) => response.data));
  }

  pspCustomerSurchargesList(params: {
    psp: number;
    customer: number;
    location: number;
  }): Observable<PspCustomerSurchargeResponse> {
    return this.pspCustomerService.getPspCustomerSurcharges(params).pipe(map((response) => response.data));
  }

  loadUserNotifications(params?: {
    page?: any;
    size?: any;
    isRead?: boolean;
  }): Observable<PaginatedApiResponse & { data?: NotificationsListResponse[] }> {
    return this.userService.getNotificationsGetList(params);
  }

  setNotificationReadState(notifications: number[]): Observable<EmptySuccessfulResponse> {
    const requestParams: { body: NotificationRecipientsUpdateRequest } = {
      body: { notificationMessageIds: notifications },
    };
    return this.userService.putNotificationsChangeReadStatus(requestParams);
  }

  setAllUserNotificationsReadStatus(): Observable<EmptySuccessfulResponse> {
    return this.userService.postNotificationsChangeAllReadStatus();
  }

  customerDepartmentFavouriteQualificationList(params: {
    customer: number;
    location: number;
    department: number;
  }): Observable<QualificationsListItemResponse[]> {
    return this.customerLocationDepartmentService
      .getCustomerLocationDepartmentQualificationListFavoriteQualifications(params)
      .pipe(map((response) => response.data.qualificationFavorites as QualificationsListItemResponse[]));
  }

  getPspBranchUsers(psp: number, branch: number): Observable<ListApiResponse & { data?: Array<UserItemFullNameType> }> {
    return this.pspBranchService.getPspsBranchUsersList({ psp, branch, active: true }).pipe(
      map((res) => {
        if (res && res?.data && res.data.length > 0) {
          return {
            ...res,
            data: res.data
              ? (res.data || []).map((user: any) => {
                  user.fullName = `${user.firstName} ${user.lastName}`;
                  return user;
                })
              : [],
          };
        }

        return res;
      })
    );
  }

  updateUserSettings(body: UserProfileSettingsUpdateRequest): Observable<EmptySuccessfulResponse> {
    return this.userService.putAppApiWebV1UserSettingUpdateIndex({ body });
  }

  getIndustrySurchargesList(): Observable<Array<CatalogIndustrySurchargeResponse>> {
    return this.catalogService.getCatalogIndustrySurchargesList().pipe(map((response) => response.data));
  }

  getCalculatedEPDate(params: {
    psp: number;
    location: number;
    profile: number;
    possibleAssignmentStartDate: string;
  }): Observable<string> {
    return this.pspCustomerLocationService
      .getAppApiWebV1PspLocationProfileEpCalculateIndex(params)
      .pipe(map((response) => response.data.ep_date as string));
  }

  getCalculatedMLPDate(params: {
    psp: number;
    location: number;
    profile: number;
    possibleAssignmentStartDate: string;
  }): Observable<string> {
    return this.pspCustomerLocationService
      .getAppApiWebV1PspLocationProfileMlpCalculateIndex(params)
      .pipe(map((response) => response.data.mlp_date as string));
  }
}
